import React from 'react';

interface Props {
  values: number[];
  width?: number;
  height?: number;
  /** Cor da linha + preenchimento. */
  color?: string;
  /** Mostra ponto destacado no último valor. */
  highlightLast?: boolean;
  ariaLabel?: string;
}

/**
 * Mini-gráfico SVG inline (sem dependência externa). Renderiza linha + área
 * preenchida muito suave. Pensado pra 6–12 pontos dentro de cards densos.
 *
 * Quando todos os valores são 0, renderiza linha reta na base.
 */
const Sparkline: React.FC<Props> = ({
  values,
  width = 120,
  height = 28,
  color = '#2563eb',
  highlightLast = true,
  ariaLabel,
}) => {
  if (values.length === 0) return null;

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const pad = 2;
  const stepX = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;

  const points = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - (v - min) / range);
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');

  // Área = linha + fechar até baseline
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(2)},${height - pad} L${points[0].x.toFixed(2)},${height - pad} Z`;

  const last = points[points.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      className="block"
    >
      <path d={areaPath} fill={color} fillOpacity={0.12} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {highlightLast && (
        <circle cx={last.x} cy={last.y} r={2.2} fill={color} />
      )}
    </svg>
  );
};

export default Sparkline;
